@use '~/vendor/cxy/scss/styles/index.scss' as cxy;

$button-height-xxs: 24px;
$button-height-xs: 28px;
$button-height-s: 32px;
$button-height: 40px;
$button-height-l: 48px;
$button-height-xl: 50px;

// A theme block sets custom properties and nothing else. A component selector here would be
// `[data-fpsm-theme='…'] .button` at (0,2,0), which outranks every (0,1,0) variant class and so
// applies to variants the theme never meant to restyle. Enforced by
// build-guards/cxy-theme-block-guard.mjs.
[data-fpsm-theme='oddschecker'] {
  --button-border-radius: var(--cxy-spacing-xxxsm);
  --button-content-color--primary: var(--cxy-color-content-feature);
  --button-fontSize: var(--cxy-fontSize-m);
  --button-fontWeight: var(--cxy-fontWeight-bold);
  --button-textTransform: uppercase;
}

[data-fpsm-theme='plus'] {
  --button-border-radius: var(--cxy-border-radius-button);
  --button-content-color--primary: var(--cxy-color-text-default-body);
  --button-fontSize: var(--cxy-fontSize-m);
  --button-lineHeight: var(--cxy-lineHeight-s);
  --button-fontWeight: var(--cxy-fontWeight-semiBold);
}

[data-fpsm-theme='whoscored'] {
  --button-border-radius: var(--cxy-spacing-xxxsm);
  --button-content-color--primary: var(--cxy-color-text-default-body);
  --button-content-color-secondary: var(--cxy-color-text-default-placeholder);
  --button-fontSize: var(--cxy-fontSize-m);
  --button-lineHeight: var(--cxy-lineHeight-s);
  --button-fontWeight: var(--cxy-fontWeight-semiBold);
}

[data-fpsm-theme='gazzetta'] {
  --button-border-radius: var(--cxy-border-radius-button);
  --button-content-color--primary: var(--cxy-color-text-default-body);
  --button-content-color-secondary: var(--cxy-color-text-default-placeholder);
  --button-fontSize: var(--cxy-fontSize-m);
  --button-lineHeight: var(--cxy-lineHeight-s);
  --button-fontWeight: var(--cxy-fontWeight-semiBold);
}

// Behaviour only — reset, hit area, disabled and the focus ring. A composed control brings its own
// skin, so nothing decorative may be added here. Everything the base used to dictate (height,
// padding, margin, transition, blanket hover) now lives in `.button` below. See NGF-7094.
.buttonBase {
  display: inline-flex;
  appearance: none;
  -webkit-box-align: center;
  align-items: center;
  -webkit-box-pack: center;
  justify-content: center;
  user-select: none;
  position: relative;
  vertical-align: middle;
  width: auto;
  border-width: 0px;
  background: none;
  cursor: pointer;

  &[disabled] {
    cursor: not-allowed;
  }

  &:focus-visible {
    @include cxy.focus-ring;
  }
}

.button {
  transition: all 250ms ease 0s;
  white-space: nowrap;
  border-radius: var(--button-border-radius, var(--cxy-border-radius-s));
  // `revert`, not `none` or `inherit`: text-transform is inherited, but the UA sheet sets it to `none`
  // on form controls and not on an anchor — so Button and Button-as-anchor want different answers and
  // only "behave as if this declaration were absent" reproduces master for both. Measured, NGF-7094.
  text-transform: var(--button-textTransform, revert);
  padding-inline: var(--cxy-spacing-sm);
  height: $button-height;
  margin: var(--cxy-spacing-0);

  &:hover {
    opacity: 0.8;
  }

  &[disabled] {
    opacity: 0.7;
  }

  &[aria-busy='true'] {
    > div {
      --base-size: 1px;
      margin-inline-start: var(--cxy-spacing-xxsm);
    }
  }
}

.xxSmallSize {
  height: $button-height-xxs;
  padding-inline: var(--cxy-spacing-xxsm);
  @include cxy.font-size('button-xxs');
}

.xSmallSize {
  height: $button-height-xs;
  padding-inline: var(--cxy-spacing-3);
  @include cxy.font-size('button-xs');
}

.smallSize {
  height: $button-height-s;
  padding-inline: var(--cxy-spacing-xsm);
  @include cxy.font-size('button-s');
}

.defaultSize {
  font-size: var(--button-fontSize, var(--cxy-fontSize-m));
  font-weight: var(--button-fontWeight, var(--cxy-fontWeight-semiBold));
  line-height: var(--button-lineHeight, var(--cxy-lineHeight-s));
}

.largeSize {
  height: $button-height-l;
  padding-inline: var(--cxy-spacing-lg);
  @include cxy.font-size('button-m', var(--cxy-fontWeight-bold));
}

.iconSize {
  height: var(--cxy-icon-size-lg);
  width: var(--cxy-icon-size-lg);
  padding: var(--cxy-spacing-xxxsm);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.primary {
  text-decoration: none;
  background: var(--button-background--primary, var(--cxy-color-interactive-primary));
  color: var(--button-content-color--primary, var(--cxy-color-content-contrast));

  &[disabled] {
    background-color: var(--cxy-color-interactive-primary-disabled);
  }
}

.secondary {
  text-decoration: none;
  color: var(--cxy-color-text-default-placeholder);
  background-color: var(--cxy-color-interactive-secondary);

  &[disabled] {
    background-color: var(--cxy-color-surface-secondary-default-subtle);
    opacity: 0.5;
  }

  &.largeSize {
    height: $button-height-xl;
  }
}

.tertiary {
  text-decoration: none;
  background-color: var(--cxy-color-interactive-tertiary);
  color: var(--cxy-color-text-default-body);

  &.largeSize {
    @include cxy.font-size('button-m');
  }

  &[disabled] {
    opacity: 0.4;
  }
}

// Selection is a state of whatever emphasis was asked for, not an emphasis in its own right — the
// `active` variant it replaces meant Pill could only be selected by giving up its own skin. At
// (0,2,0) this beats the emphasis class whatever the emit order, which the retired variant did not.
.tertiary[data-state='on'] {
  background-color: var(--cxy-color-surface-secondary-default-subtle);
  color: var(--cxy-color-text-default-placeholder);

  // `active` REPLACED `.tertiary` rather than joining it, so a selected Pill never picked up
  // `.tertiary[disabled]`'s 0.4 and fell through to `.button[disabled]`'s 0.7. Held, so the change
  // of axis does not also restyle disabled pills.
  &[disabled] {
    opacity: 0.7;
  }
}

.ghost,
.ghostBrand {
  text-decoration: underline;
  padding: 0;
  border-radius: 0;
  background: transparent;
  padding: 0;
  height: max-content;

  &[disabled] {
    color: var(--cxy-color-text-default-body);
    opacity: 0.4;
  }

  &.smallSize {
    @include cxy.font-size('button-xs');
  }

  &.defaultSize {
    @include cxy.font-size('button-s');
  }

  &.largeSize {
    @include cxy.font-size('button-m');
  }
}

.ghost {
  &,
  &:visited {
    color: var(--cxy-color-text-default-body);
  }
}

.ghostBrand {
  &,
  &:visited {
    color: var(--cxy-color-brand);
  }
}

.outline {
  outline: 2px solid var(--cxy-color-brand);
  outline-offset: 2px;
  background-color: transparent;
  color: var(--cxy-color-text-default-body);

  &.largeSize {
    @include cxy.font-size('button-m');
    height: $button-height-xl;
  }
}

.surface {
  text-decoration: none;
  background-color: var(--cxy-color-surface-page);
  color: var(--cxy-color-text-default-placeholder);
}

.isFullWidth {
  width: 100%;
}

.rounded {
  border-radius: var(--cxy-border-radius-pill, 94px);
}
